package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.FirebaseStorageService
import co.uk.sacursoftware.ssinventory.util.StorageArea
import co.uk.sacursoftware.ssinventory.util.getCurrentLocalDate
import co.uk.sacursoftware.ssinventory.util.rememberPlatformImagePicker
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import androidx.compose.foundation.Image
import androidx.compose.ui.layout.ContentScale
import org.jetbrains.compose.resources.painterResource
import ssinventory.shared.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddEditServiceScreen(
    service: Service?,
    onBack: () -> Unit,
    onSaved: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val storageService = remember { FirebaseStorageService() }

    val nowDate = getCurrentLocalDate()
    val defaultDate = service?.serviceDateTime?.substringBefore(" ") ?: "${nowDate.year}-${(nowDate.month.ordinal + 1).toString().padStart(2, '0')}-${nowDate.dayOfMonth.toString().padStart(2, '0')}"
    val defaultTime = service?.serviceDateTime?.substringAfter(" ") ?: "12:00"
    val defaultHour = defaultTime.split(":").getOrNull(0) ?: "12"
    val defaultMinute = defaultTime.split(":").getOrNull(1) ?: "00"

    var title by remember { mutableStateOf(service?.title ?: "") }
    var description by remember { mutableStateOf(service?.description ?: "") }
    var dateInput by remember { mutableStateOf(defaultDate) }
    var hourInput by remember { mutableStateOf(defaultHour) }
    var minuteInput by remember { mutableStateOf(defaultMinute) }
    var isDone by remember { mutableStateOf(service?.isDone == 1) }
    var completionNotes by remember { mutableStateOf(service?.completionNotes ?: "") }
    var outstandingNotes by remember { mutableStateOf(service?.outstandingNotes ?: "") }

    val images = remember { mutableStateListOf<String>().apply { addAll(service?.images ?: emptyList()) } }
    var isUploading by remember { mutableStateOf(false) }
    var isSaving by remember { mutableStateOf(false) }
    var showDatePicker by remember { mutableStateOf(false) }
    var pendingImageIndex by remember { mutableStateOf<Int?>(null) }

    val imagePicker = rememberPlatformImagePicker { bytes, _ ->
        scope.launch {
            isUploading = true
            try {
                val url = storageService.uploadImage(bytes, StorageArea.SERVICES)
                images.add(url)
            } catch (_: Exception) {}
            isUploading = false
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { Text(if (service == null) "Add Service" else "Edit Service") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                },
                actions = {
                    Button(
                        onClick = {
                            if (title.isNotBlank()) {
                                val serviceDateTime = "$dateInput $hourInput:$minuteInput"
                                val req = SaveServiceRequest(
                                    id = service?.id,
                                    title = title.trim(),
                                    description = description.ifBlank { null },
                                    serviceDateTime = serviceDateTime,
                                    isDone = if (isDone) 1 else 0,
                                    completionNotes = completionNotes.ifBlank { null },
                                    outstandingNotes = outstandingNotes.ifBlank { null },
                                    images = images.toList()
                                )
                                scope.launch {
                                    isSaving = true
                                    repository.saveService(req).onSuccess {
                                        onSaved()
                                    }.onFailure {
                                        isSaving = false
                                        snackbarHostState.showSnackbar("Failed to save service")
                                    }
                                }
                            }
                        },
                        enabled = title.isNotBlank() && !isSaving,
                        modifier = Modifier.padding(end = 8.dp),
                        shape = MaterialTheme.shapes.small
                    ) {
                        if (isSaving) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), color = MaterialTheme.colorScheme.onPrimary)
                        } else {
                            Text("SAVE")
                        }
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            OutlinedTextField(
                value = title,
                onValueChange = { title = it },
                label = { Text("Service Title *") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                shape = MaterialTheme.shapes.small
            )

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text("Description", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                OutlinedTextField(
                    value = description,
                    onValueChange = { description = it },
                    label = { Text("Enter detailed description...") },
                    modifier = Modifier.fillMaxWidth().height(130.dp),
                    minLines = 4,
                    maxLines = 8,
                    shape = MaterialTheme.shapes.small
                )
            }

            // Date Picker Section
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text("Service Date", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                OutlinedButton(
                    onClick = { showDatePicker = true },
                    modifier = Modifier.fillMaxWidth(),
                    shape = MaterialTheme.shapes.small
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Image(
                            painter = painterResource(Res.drawable.calendar),
                            contentDescription = "Date",
                            modifier = Modifier.size(20.dp),
                            contentScale = ContentScale.Fit
                        )
                        Text("Date: $dateInput (Tap to change)")
                    }
                }
            }

            // Modern Time Picker Section
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("Service Time (Hour & Minutes)", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                
                Text("Hour (00-23): $hourInput", style = MaterialTheme.typography.bodySmall)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    (0..23).forEach { h ->
                        val hStr = h.toString().padStart(2, '0')
                        FilterChip(
                            selected = hStr == hourInput,
                            onClick = { hourInput = hStr },
                            label = { Text(hStr) }
                        )
                    }
                }

                Text("Minute:", style = MaterialTheme.typography.bodySmall)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    listOf("00", "15", "30", "45").forEach { m ->
                        FilterChip(
                            selected = m == minuteInput,
                            onClick = { minuteInput = m },
                            label = { Text(m) },
                            modifier = Modifier.weight(1f)
                        )
                    }
                }
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text("Is Done?", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                Checkbox(
                    checked = isDone,
                    onCheckedChange = { isDone = it }
                )
            }

            OutlinedTextField(
                value = completionNotes,
                onValueChange = { completionNotes = it },
                label = { Text("Completion Notes") },
                modifier = Modifier.fillMaxWidth(),
                minLines = 2,
                maxLines = 4,
                shape = MaterialTheme.shapes.small
            )

            OutlinedTextField(
                value = outstandingNotes,
                onValueChange = { outstandingNotes = it },
                label = { Text("Outstanding Notes") },
                modifier = Modifier.fillMaxWidth(),
                minLines = 2,
                maxLines = 4,
                shape = MaterialTheme.shapes.small
            )

            // Images Section
            Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("Photos & Attachments (${images.size})", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedButton(
                        onClick = { imagePicker.takePhoto() },
                        modifier = Modifier.weight(1f),
                        enabled = !isUploading
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Image(
                                painter = painterResource(Res.drawable.delivery),
                                contentDescription = "Camera",
                                modifier = Modifier.size(20.dp),
                                contentScale = ContentScale.Fit
                            )
                            Text("Camera")
                        }
                    }
                    OutlinedButton(
                        onClick = { imagePicker.pickFromGallery() },
                        modifier = Modifier.weight(1f),
                        enabled = !isUploading
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Image(
                                painter = painterResource(Res.drawable.folder),
                                contentDescription = "Gallery",
                                modifier = Modifier.size(20.dp),
                                contentScale = ContentScale.Fit
                            )
                            Text("Gallery")
                        }
                    }
                }
                if (isUploading) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        CircularProgressIndicator(modifier = Modifier.size(20.dp))
                        Text("Uploading photo...", style = MaterialTheme.typography.bodySmall)
                    }
                }
                if (images.isNotEmpty()) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        images.forEachIndexed { index, imgUrl ->
                            Card(
                                modifier = Modifier.fillMaxWidth(),
                                shape = MaterialTheme.shapes.small,
                                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(8.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                        Box(modifier = Modifier.size(50.dp)) {
                                            RemoteImageView(url = imgUrl, modifier = Modifier.fillMaxSize())
                                        }
                                        Text(
                                            text = "Photo ${index + 1}",
                                            style = MaterialTheme.typography.bodyMedium,
                                            maxLines = 1
                                        )
                                    }
                                    TextButton(onClick = { pendingImageIndex = index }) {
                                        Text("Remove", color = MaterialTheme.colorScheme.error)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (showDatePicker) {
            val parsedDate = try {
                LocalDate.parse(dateInput)
            } catch (_: Exception) {
                getCurrentLocalDate()
            }
            ServiceDatePickerDialog(
                initialDate = parsedDate,
                onDismiss = { showDatePicker = false },
                onSelect = { selectedDate ->
                    dateInput = "${selectedDate.year}-${selectedDate.month.number.toString().padStart(2, '0')}-${selectedDate.dayOfMonth.toString().padStart(2, '0')}"
                    showDatePicker = false
                }
            )
        }

        pendingImageIndex?.let { index ->
            ConfirmDeleteDialog(
                title = "Remove photo?",
                message = "Remove photo ${index + 1}?",
                confirmLabel = "Remove",
                onConfirm = {
                    if (index in images.indices) images.removeAt(index)
                    pendingImageIndex = null
                },
                onDismiss = { pendingImageIndex = null }
            )
        }
    }
}

@Composable
fun ServiceDatePickerDialog(
    initialDate: LocalDate,
    onDismiss: () -> Unit,
    onSelect: (LocalDate) -> Unit
) {
    var selectedYear by remember { mutableStateOf(initialDate.year) }
    var selectedMonth by remember { mutableStateOf(initialDate.month.number) }
    var selectedDay by remember { mutableStateOf(initialDate.dayOfMonth) }

    val years = (2024..2030).toList()
    val months = (1..12).toList()
    val days = (1..31).toList()

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Select Service Date") },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Text("Year: $selectedYear", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                Row(modifier = Modifier.horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    years.forEach { y ->
                        FilterChip(
                            selected = y == selectedYear,
                            onClick = { selectedYear = y },
                            label = { Text("$y") }
                        )
                    }
                }

                Text("Month: $selectedMonth", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                Row(modifier = Modifier.horizontalScroll(rememberScrollState()), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    months.forEach { m ->
                        val monthName = Month(m).name.lowercase().replaceFirstChar { it.uppercase() }.take(3)
                        FilterChip(
                            selected = m == selectedMonth,
                            onClick = { selectedMonth = m },
                            label = { Text(monthName) }
                        )
                    }
                }

                Text("Day: $selectedDay", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                Box(modifier = Modifier.heightIn(max = 160.dp).verticalScroll(rememberScrollState())) {
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        days.chunked(7).forEach { weekDays ->
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                weekDays.forEach { d ->
                                    val isSelected = d == selectedDay
                                    Button(
                                        onClick = { selectedDay = d },
                                        modifier = Modifier.weight(1f).height(40.dp),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = if (isSelected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceVariant
                                        ),
                                        contentPadding = PaddingValues(0.dp)
                                    ) {
                                        Text(
                                            text = "$d",
                                            color = if (isSelected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant,
                                            style = MaterialTheme.typography.bodySmall
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = {
                    try {
                        val d = LocalDate(selectedYear, selectedMonth, selectedDay.coerceIn(1, 31))
                        onSelect(d)
                    } catch (_: Exception) {
                        onSelect(initialDate)
                    }
                }
            ) {
                Text("Confirm")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}
